// WhatsApp set-up and templates in the app (docs/specs/WHATSAPP.md; steps 0.30, 0.31). Types for crm_whatsapp_account
// and crm_whatsapp_templates (migration 20261123090000), labels, and Meta's Embedded Signup in the browser.
//
// Embedded Signup, as Meta documents it (checked 11 Oct 2026): load https://connect.facebook.net/en_US/sdk.js, call
// FB.init({appId, autoLogAppEvents, xfbml, version}), then FB.login(callback, {config_id, response_type: "code",
// override_default_response_type: true, extras: {setup: {}}}). The callback gets authResponse.code (it lasts 30
// seconds), and Meta's window posts a message {type: "WA_EMBEDDED_SIGNUP", event: "FINISH" |
// "FINISH_WHATSAPP_BUSINESS_APP_ONBOARDING" | "CANCEL" | "ERROR", data: {phone_number_id, waba_id, business_id}} from a
// facebook.com origin. UNVERIFIED: where Embedded Signup v4 takes the coexistence option; Meta's v3 pages put
// featureType: "whatsapp_business_app_onboarding" in extras, and that is what is sent here.

export interface ChecklistItem {
  key: "connected" | "business_verification" | "display_name" | "payment_method";
  label: string;
  done: boolean;
}

export interface WhatsAppAccount {
  waba_id: string;
  phone_number_id: string;
  business_id?: string | null;
  display_phone_number?: string | null;
  display_name?: string | null;
  name_status?: string | null;
  quality_rating?: string | null;
  status: "connecting" | "connected" | "restricted" | "disconnected";
  coexistence: boolean;
  business_verification: string;
  checklist: Partial<Record<"business_verification" | "display_name" | "payment_method", boolean>>;
  checklist_items: ChecklistItem[];
  has_token: boolean;
  connected_at?: string;
}

export interface WhatsAppTemplate {
  id: string;
  key: string;
  name: string;
  language: string;
  category: "utility" | "marketing";
  body: string;
  variables: { name: string; example: string }[];
  footer?: string | null;
  status: "draft" | "approved" | "declined" | "submitted";
  meta_status?: string | null;
  meta_reason?: string | null;
  meta_category?: string | null;
  meta_checked_at?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  updated_at?: string;
}

export interface SignupConfig {
  available: boolean;
  app_id: string | null;
  config_id: string | null;
  graph_version: string;
  /** Demo mode: no Meta window, the connection is simulated. */
  demo?: boolean;
}

export interface SignupResult {
  code: string;
  waba_id: string;
  phone_number_id: string;
  business_id?: string;
  coexistence: boolean;
}

/** What each template is for, in the order the playbook uses them. */
export const TEMPLATE_KEYS: { key: string; label: string; hint: string }[] = [
  { key: "enquiry_reply", label: "Enquiry reply", hint: "The instant reply to a new enquiry (Settings › Campaign defaults › Speed to lead)" },
  { key: "booking_offer", label: "Booking offer", hint: "Offers times to someone who enquired" },
  { key: "booking_confirmation", label: "Booking confirmation", hint: "Confirms a booked meeting" },
  { key: "reminder", label: "Reminder", hint: "Reminds them before the meeting" },
  { key: "follow_up", label: "Follow-up", hint: "Checks in after an enquiry, without selling" },
  { key: "opted_in_update", label: "Opted-in update", hint: "News for people who opted in (marketing)" },
  // 10.19 only when the happiness check is on (engine/whatsapp.py needs)
  { key: "happiness_check", label: "How did we do?", hint: "Asks a customer how it went before the review request; never asks for a review" },
];

export function templateLabel(key: string): string {
  return TEMPLATE_KEYS.find((k) => k.key === key)?.label ?? key.replace(/_/g, " ");
}

/** Where a template stands, in words: ours first, then Meta's review. */
export function templateState(t: Pick<WhatsAppTemplate, "status" | "meta_status">): { label: string; tone: "wait" | "ok" | "bad" | "plain" } {
  if (t.status === "draft") return { label: "Waiting for you", tone: "wait" };
  if (t.status === "declined") return { label: "Declined", tone: "plain" };
  if (t.status === "approved") return { label: "Approved, not yet with Meta", tone: "wait" };
  switch (t.meta_status) {
    case "APPROVED": return { label: "Approved by Meta", tone: "ok" };
    case "PENDING": case "IN_APPEAL": return { label: "In review at Meta", tone: "wait" };
    case "REJECTED": return { label: "Rejected by Meta", tone: "bad" };
    case "PAUSED": return { label: "Paused by Meta", tone: "bad" };
    case "DISABLED": return { label: "Disabled by Meta", tone: "bad" };
    default: return { label: t.meta_status ? `Meta: ${t.meta_status.toLowerCase().replace(/_/g, " ")}` : "With Meta", tone: "wait" };
  }
}

/** The body with each {{n}} replaced by its example, for a preview. */
export function preview(t: Pick<WhatsAppTemplate, "body" | "variables">): string {
  return t.body.replace(/\{\{\s*(\d{1,2})\s*\}\}/g, (m, n: string) => t.variables[Number(n) - 1]?.example ?? m);
}

/** The placeholders a body uses, so an edit can be checked before saving. */
export function placeholders(body: string): number[] {
  return [...new Set([...body.matchAll(/\{\{\s*(\d{1,2})\s*\}\}/g)].map((m) => Number(m[1])))].sort((a, b) => a - b);
}

export function editProblem(body: string, variables: number): string | null {
  if (!body.trim()) return "The message can't be empty.";
  if (body.length > 1024) return "A template is at most 1,024 characters.";
  const p = placeholders(body);
  if (p.length !== variables || p.some((n, i) => n !== i + 1)) {
    return variables ? `Keep the placeholders {{1}} to {{${variables}}}, each once or more.` : "This template has no placeholders.";
  }
  return null;
}

/** True for a message from Meta's own window (https, facebook.com or a subdomain). */
export function fromFacebook(origin: string): boolean {
  try {
    const u = new URL(origin);
    return u.protocol === "https:" && (u.hostname === "facebook.com" || u.hostname.endsWith(".facebook.com"));
  } catch {
    return false;
  }
}

export interface FbSdk {
  init(o: Record<string, unknown>): void;
  login(cb: (r: { authResponse?: { code?: string } | null }) => void, o: Record<string, unknown>): void;
}

export type Win = Window & { FB?: FbSdk; fbAsyncInit?: () => void };

const SDK_URL = "https://connect.facebook.net/en_US/sdk.js";

export function loadSdk(win: Win, appId: string, version: string): Promise<FbSdk> {
  if (win.FB) return Promise.resolve(win.FB);
  return new Promise((resolve, reject) => {
    win.fbAsyncInit = () => {
      win.FB!.init({ appId, autoLogAppEvents: true, xfbml: true, version });
      resolve(win.FB!);
    };
    const s = win.document.createElement("script");
    s.src = SDK_URL;
    s.async = true;
    s.defer = true;
    s.crossOrigin = "anonymous";
    s.onerror = () => reject(new Error("Meta's sign-up window couldn't load. Check that nothing blocks facebook.com and try again."));
    win.document.body.appendChild(s);
  });
}

/**
 * Opens Meta's Embedded Signup and resolves with the code and the ids Meta reported, once both have arrived.
 * Rejects when the person cancels or Meta reports an error.
 */
export async function launchEmbeddedSignup(cfg: SignupConfig, coexistence: boolean, win: Win = window as Win, timeoutMs = 30 * 60_000): Promise<SignupResult> {
  if (!cfg.app_id || !cfg.config_id) throw new Error("Connecting WhatsApp isn't switched on for this server yet.");
  const fb = await loadSdk(win, cfg.app_id, cfg.graph_version);
  return new Promise<SignupResult>((resolve, reject) => {
    let code: string | null = null;
    let ids: { waba_id: string; phone_number_id: string; business_id?: string } | null = null;
    const done = (err?: Error) => {
      win.removeEventListener("message", onMessage);
      clearTimeout(timer);
      if (err) reject(err);
      else resolve({ code: code!, ...ids!, coexistence });
    };
    const maybe = () => {
      if (code && ids) done();
    };
    const onMessage = (e: MessageEvent) => {
      if (!fromFacebook(e.origin)) return;
      let msg: { type?: string; event?: string; data?: Record<string, unknown> };
      try {
        msg = typeof e.data === "string" ? JSON.parse(e.data) : e.data;
      } catch {
        return;
      }
      if (msg?.type !== "WA_EMBEDDED_SIGNUP") return;
      if (msg.event === "CANCEL") return done(new Error("Sign-up was closed before it finished. Press Connect WhatsApp to try again."));
      if (msg.event === "ERROR") return done(new Error(`Meta reported a problem: ${String(msg.data?.error_message ?? "unknown error").slice(0, 200)}`));
      if (typeof msg.event === "string" && msg.event.startsWith("FINISH")) {
        const d = msg.data ?? {};
        if (typeof d.waba_id === "string" && typeof d.phone_number_id === "string") {
          ids = { waba_id: d.waba_id, phone_number_id: d.phone_number_id, ...(typeof d.business_id === "string" ? { business_id: d.business_id } : {}) };
          maybe();
        } else {
          done(new Error("Meta finished without a phone number. Add a number in the sign-up window."));
        }
      }
    };
    const timer = setTimeout(() => done(new Error("Sign-up took too long. Press Connect WhatsApp to try again.")), timeoutMs);
    win.addEventListener("message", onMessage);
    fb.login((r) => {
      const c = r?.authResponse?.code;
      if (typeof c === "string" && c) {
        code = c;
        maybe();
      } else if (!ids) {
        done(new Error("Sign-up was closed before it finished. Press Connect WhatsApp to try again."));
      }
    }, {
      config_id: cfg.config_id,
      response_type: "code",
      override_default_response_type: true,
      extras: coexistence ? { setup: {}, featureType: "whatsapp_business_app_onboarding" } : { setup: {} },
    });
  });
}
